import api from '@/services/api';
import type {
  Appointment,
  CreateAppointmentRequest,
  RescheduleRequest,
  CancelRequest,
  DailySchedule,
  AvailableTimeSlot,
  ClinicDashboard,
} from '@/types/appointment';

const BASE_PATH = '/appointments';
const SCHEDULES_PATH = '/schedules';

/**
 * Appointment management API service.
 *
 * Handles creation, status transitions, scheduling views,
 * and available time slot queries.
 */
export const appointmentService = {
  /**
   * Creates a new appointment.
   */
  async createAppointment(payload: CreateAppointmentRequest): Promise<Appointment> {
    const { data } = await api.post<Appointment>(BASE_PATH, payload);
    return data;
  },

  /**
   * Reschedules an appointment to a new date/time/resource.
   */
  async reschedule(id: string, payload: RescheduleRequest): Promise<Appointment> {
    const { data } = await api.put<Appointment>(`${BASE_PATH}/${id}/reschedule`, payload);
    return data;
  },

  /**
   * Cancels an appointment with a reason.
   */
  async cancel(id: string, payload: CancelRequest): Promise<Appointment> {
    const { data } = await api.put<Appointment>(`${BASE_PATH}/${id}/cancel`, payload);
    return data;
  },

  /**
   * Confirms an appointment.
   */
  async confirm(id: string): Promise<Appointment> {
    const { data } = await api.put<Appointment>(`${BASE_PATH}/${id}/confirm`);
    return data;
  },

  /**
   * Checks in a patient (marks as WAITING).
   */
  async checkIn(id: string): Promise<Appointment> {
    const { data } = await api.put<Appointment>(`${BASE_PATH}/${id}/check-in`);
    return data;
  },

  /**
   * Starts an appointment (marks as IN_PROGRESS).
   */
  async start(id: string): Promise<Appointment> {
    const { data } = await api.put<Appointment>(`${BASE_PATH}/${id}/start`);
    return data;
  },

  /**
   * Completes an appointment.
   */
  async complete(id: string): Promise<Appointment> {
    const { data } = await api.put<Appointment>(`${BASE_PATH}/${id}/complete`);
    return data;
  },

  /**
   * Marks an appointment as no-show.
   */
  async markNoShow(id: string): Promise<Appointment> {
    const { data } = await api.put<Appointment>(`${BASE_PATH}/${id}/no-show`);
    return data;
  },

  /**
   * Fetches the daily schedule for a specific resource and date.
   */
  async getDailySchedule(resourceId: string, date: string): Promise<DailySchedule> {
    const { data } = await api.get<DailySchedule>(`${SCHEDULES_PATH}/daily`, {
      params: { resourceId, date },
    });
    return data;
  },

  /**
   * Fetches available time slots for a resource on a given date,
   * considering procedure type duration.
   */
  async getAvailableSlots(
    resourceId: string,
    date: string,
    procedureTypeId: string,
  ): Promise<AvailableTimeSlot[]> {
    const { data } = await api.get<AvailableTimeSlot[]>(`${SCHEDULES_PATH}/available-slots`, {
      params: { resourceId, date, procedureTypeId },
    });
    return data;
  },

  /**
   * Fetches the clinic dashboard for a given date,
   * including summary counts and all appointments.
   */
  async getClinicDashboard(date: string): Promise<ClinicDashboard> {
    const { data } = await api.get<ClinicDashboard>(`${SCHEDULES_PATH}/dashboard`, {
      params: { date },
    });
    return data;
  },
};
